import * as Sentry from '@sentry/react';
import mixpanel from 'mixpanel-browser';

import {
  receivedAccountActivationFail,
  receivedAccountActivationSuccess,
  receivedAuthenticationFail,
  receivedAuthenticationSuccess,
  receivedClearNetworkError,
  receivedContactlessAuthFail,
  receivedContactlessAuthSuccess,
  receivedListAccessFail,
  receivedListAccessSuccess,
  receivedLogoutSuccess,
  receivedPasswordResetFail,
  receivedPasswordResetRequestFail,
  receivedPasswordResetRequestSuccess,
  receivedPasswordResetSuccess,
  receivedWhoamiFail,
  receivedWhoamiSuccess,
  startLoading,
} from './actions';
import { receivedGetDealershipSettingsSuccess } from 'services/DealershipSettings/actions';

import { changeLocale } from 'services/Language/actions';

import {
  accountActivation,
  authenticate,
  authenticateGuestToken,
  listAccess,
  passwordReset,
  passwordResetRequest,
  retrieveAuthenticatedUser,
} from 'services/Authentication/resources';

import { getUserViews } from 'services/Views/thunks';
import { getFleetSettings } from 'services/FleetSettings/thunks';

import { updateAbility } from 'permissions';

import { FLASH_MESSAGE_TYPES, USER_ROLES } from 'constants/common';
import { intercom, mixpanel as mixpanelUtils } from 'utils';
import { history } from '../../store';

import { FLEET_PLANS } from '../../constants/plans';
import { updateCustomer } from 'services/Public/thunks';
import { UserAccess } from 'services/resources';
import { historyPush } from 'utils/historyPush.utils';
import { listHierarchy } from 'services/Hierarchy/thunks';

function setSentryUserContext(data) {
  const { id, email, profile: { dealership: { name: dealership = null } = {} } = {} } = data;

  Sentry.configureScope((scope) => {
    scope.setUser({ id, email });
    scope.setExtra('dealership', dealership);
  });
}

/**
 * Checks the user authentication token
 */
export const whoami = () => (dispatch) =>
  retrieveAuthenticatedUser()
    .then((response) => {
      setSentryUserContext(response);
      return Promise.all([
        dispatch(getUserViews(response.id)),
        dispatch(receivedGetDealershipSettingsSuccess(response.profile.dealership.settings)),
        dispatch(getFleetSettings()),
        dispatch(
          changeLocale(`${response.profile.language}-${response.profile.dealership.settings.country}` || 'en-CA')
        ),
        dispatch(listHierarchy()),
      ]).then(() => {
        if (response.email) {
          // Do not identify Guest Users
          intercom.authenticate(response);
          mixpanelUtils.identifyAndAssign(response);
        }

        updateAbility(response);

        // Calling success on Whoami after all app-related states have been retrieved.
        // This ensures all app required state is loaded. (i.e. dealershipSettings, which
        // is required for proper "home" navigation)
        dispatch(receivedWhoamiSuccess(response));
      });
    })
    .catch((response) => Promise.all([dispatch(receivedWhoamiFail(response.data)), historyPush(history, '/login')]));

/**
 * Authenticates a user
 */
export const authenticateUser = (username: string, password: string) => (dispatch) => {
  dispatch(startLoading());

  return (
    authenticate(username, password)
      // @ts-expect-error TS(2339) Property 'token' does not exist on type 'unknown'.
      .then((response) => Promise.all([dispatch(receivedAuthenticationSuccess(response.token)), dispatch(whoami())]))
      .catch((response) => {
        dispatch(receivedAuthenticationFail(response.data));
      })
  );
};

/**
 * Authenticate a contactless customer
 */
export const authenticateContactless = (contactlessToken: string) => (dispatch) => {
  dispatch(startLoading());

  mixpanel.disable();

  return authenticateGuestToken(contactlessToken)
    .then((response) => {
      // @ts-expect-error TS(2339) Property 'token' does not exist on type 'unknown'.
      dispatch(receivedContactlessAuthSuccess(response.token));
      updateAbility({
        id: 0,
        firstName: '',
        lastName: '',
        email: '',
        intercomHash: '',
        isActive: true,
        profile: {
          dealership: {
            settings: {
              planId: FLEET_PLANS.standard,
            },
          },
          role: USER_ROLES.signingCustomer,
        },
      });
    })
    .catch((response) => {
      dispatch(receivedContactlessAuthFail(response.data));
    });
};

/**
 * Invalidates a user token
 */
export const logoutUser =
  (forceReload = false, reroute = true) =>
  (dispatch) => {
    dispatch(receivedLogoutSuccess());

    mixpanel.reset(); // Clear mixpanel user, generate new random distinct_id

    intercom.shutdown();
    intercom.boot();

    if (reroute) {
      historyPush(history, '/login');
    }

    if (forceReload) {
      window.location.reload();
    }
  };

/**
 * Sends a password reset request
 */
export const userPasswordResetRequest = (email: string) => (dispatch) => {
  dispatch(startLoading());

  return passwordResetRequest(email)
    .then((response) => {
      // @ts-expect-error TS(2339) Property 'error' does not exist on type 'unknown'.
      if (response.error) {
        // @ts-expect-error TS(2339) Property 'error' does not exist on type 'unknown'.
        dispatch(receivedPasswordResetRequestFail(response.error));
      } else {
        dispatch(receivedPasswordResetRequestSuccess(response));
      }
    })
    .catch((response) => {
      dispatch(receivedPasswordResetRequestFail(response.data));
    });
};

/**
 * Resets the user password
 */
export const userPasswordReset =
  (uid: string, token: string, password: string, passwordConfirmation: string) => (dispatch) => {
    dispatch(startLoading());

    return passwordReset(uid, token, password, passwordConfirmation)
      .then((response) => {
        // @ts-expect-error TS(2339) Property 'error' does not exist on type 'unknown'.
        if (response.error) {
          // @ts-expect-error TS(2339) Property 'error' does not exist on type 'unknown'.
          dispatch(receivedPasswordResetFail(response.error));
        } else {
          dispatch(receivedPasswordResetSuccess(response));
        }
      })
      .catch((response) => {
        dispatch(receivedPasswordResetFail(response.data));
      });
  };

/**
 * Set names and new password
 */
export const userAccountActivation =
  (firstname: string, lastname: string, password: string, passwordConfirmation: string, token: string, uid: string) =>
  (dispatch) => {
    dispatch(startLoading());

    return accountActivation(firstname, lastname, password, passwordConfirmation, token, uid)
      .then(async (response) => {
        // @ts-expect-error TS(2339) Property 'error' does not exist on type 'unknown'.
        if (response.error) {
          // @ts-expect-error TS(2339) Property 'error' does not exist on type 'unknown'.
          await dispatch(receivedAccountActivationFail(response.error));
        } else {
          await dispatch(receivedAccountActivationSuccess(response));
          dispatch(
            history.push('/login', {
              flashMessage: {
                type: FLASH_MESSAGE_TYPES.success,
                translationId: 'views.AccountSettings.Creation.success',
              },
            })
          );
        }
      })
      .catch((response) => {
        dispatch(receivedAccountActivationFail(response.data));
      });
  };

/**
 * Clear the network error.
 */
export const clearNetworkError = () => (dispatch) => dispatch(receivedClearNetworkError());

/**
 * Get the list of access
 */
export const getListAccess = (authToken: string) => (dispatch) => {
  return listAccess(authToken)
    .then((response: UserAccess) => {
      dispatch(receivedListAccessSuccess(response));
      return response;
    })
    .catch((response) => {
      dispatch(receivedListAccessFail(response.data));
      throw response;
    });
};
